import { z } from 'zod';
import { queryBoolean } from '../api/query-boolean';

/**
 * The Academic service's public contract.
 *
 * A church can run more than one independent School, each with its own
 * pensum (Levels, and the Subjects required at each), Teachers and
 * CourseSections (a term's offering of a Subject). Every reference to a
 * Person — a student's enrollment, a teacher, whoever requested or reviewed
 * a homologation — is a plain UUID, resolved through PeopleReadPort, the
 * same rule every dependent module follows. A homologation's supporting
 * certificate is a plain UUID into Files for the same reason. Attendance for
 * a class session is not modeled here: a course section is just another
 * context Attendance's own contract knows about.
 */

export const COURSE_SECTION_STATUSES = ['ACTIVE', 'ARCHIVED'] as const;
export type CourseSectionStatus = (typeof COURSE_SECTION_STATUSES)[number];

export const ENROLLMENT_STATUSES = ['ENROLLED', 'COMPLETED', 'WITHDRAWN'] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

export const HOMOLOGATION_SOURCE_TYPES = ['INTERNAL_SCHOOL', 'EXTERNAL_INSTITUTION'] as const;
export type HomologationSourceType = (typeof HOMOLOGATION_SOURCE_TYPES)[number];

export const HOMOLOGATION_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'] as const;
export type HomologationStatus = (typeof HOMOLOGATION_STATUSES)[number];

const gradeField = z.number().min(0).max(100);

// --- Schools ------------------------------------------------------------------

const schoolNameField = z.string().trim().min(1).max(150);

export const createSchoolRequestSchema = z.object({
  name: schoolNameField,
  description: z.string().trim().max(2000).nullish(),
});
export type CreateSchoolRequest = z.infer<typeof createSchoolRequestSchema>;

export const updateSchoolRequestSchema = z
  .object({
    name: schoolNameField.optional(),
    description: z.string().trim().max(2000).nullish(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateSchoolRequest = z.infer<typeof updateSchoolRequestSchema>;

export const listSchoolsQuerySchema = z.object({
  includeArchived: queryBoolean().default(false),
});
export type ListSchoolsQuery = z.infer<typeof listSchoolsQuerySchema>;

export interface SchoolResponse {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly archivedAt: string | null;
}

// --- Levels -------------------------------------------------------------------

const levelNameField = z.string().trim().min(1).max(150);

export const createAcademicLevelRequestSchema = z.object({
  name: levelNameField,
  order: z.number().int().default(0),
});
export type CreateAcademicLevelRequest = z.infer<typeof createAcademicLevelRequestSchema>;

export const updateAcademicLevelRequestSchema = z
  .object({
    name: levelNameField.optional(),
    order: z.number().int().optional(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateAcademicLevelRequest = z.infer<typeof updateAcademicLevelRequestSchema>;

export const listAcademicLevelsQuerySchema = z.object({
  includeArchived: queryBoolean().default(false),
});
export type ListAcademicLevelsQuery = z.infer<typeof listAcademicLevelsQuerySchema>;

export interface AcademicLevelResponse {
  readonly id: string;
  readonly schoolId: string;
  readonly name: string;
  readonly order: number;
  readonly archivedAt: string | null;
}

// --- Subjects ("Materias") ------------------------------------------------------

const subjectNameField = z.string().trim().min(1).max(150);

export const createSubjectRequestSchema = z.object({
  levelId: z.uuid(),
  name: subjectNameField,
  description: z.string().trim().max(2000).nullish(),
  credits: z.number().int().positive().nullish(),
  prerequisiteSubjectId: z.uuid().nullish(),
  /** A `GrowthStep` id — completing this subject advances the student there
   *  automatically. Null means this subject carries no growth-path meaning. */
  linkedGrowthStepId: z.uuid().nullish(),
});
export type CreateSubjectRequest = z.infer<typeof createSubjectRequestSchema>;

export const updateSubjectRequestSchema = z
  .object({
    levelId: z.uuid().optional(),
    name: subjectNameField.optional(),
    description: z.string().trim().max(2000).nullish(),
    credits: z.number().int().positive().nullish(),
    prerequisiteSubjectId: z.uuid().nullish(),
    linkedGrowthStepId: z.uuid().nullish(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateSubjectRequest = z.infer<typeof updateSubjectRequestSchema>;

export const listSubjectsQuerySchema = z.object({
  includeArchived: queryBoolean().default(false),
});
export type ListSubjectsQuery = z.infer<typeof listSubjectsQuerySchema>;

export interface SubjectResponse {
  readonly id: string;
  readonly schoolId: string;
  readonly levelId: string;
  readonly name: string;
  readonly description: string | null;
  readonly credits: number | null;
  readonly prerequisiteSubjectId: string | null;
  readonly linkedGrowthStepId: string | null;
  readonly archivedAt: string | null;
}

// --- Teachers -------------------------------------------------------------------

export const createTeacherRequestSchema = z.object({
  personId: z.uuid(),
  specialty: z.string().trim().max(200).nullish(),
  bio: z.string().trim().max(2000).nullish(),
  subjectIds: z.array(z.uuid()).default([]),
});
export type CreateTeacherRequest = z.infer<typeof createTeacherRequestSchema>;

export const updateTeacherRequestSchema = z
  .object({
    specialty: z.string().trim().max(200).nullish(),
    bio: z.string().trim().max(2000).nullish(),
    subjectIds: z.array(z.uuid()).optional(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateTeacherRequest = z.infer<typeof updateTeacherRequestSchema>;

export const listTeachersQuerySchema = z.object({
  includeArchived: queryBoolean().default(false),
});
export type ListTeachersQuery = z.infer<typeof listTeachersQuerySchema>;

export interface TeacherResponse {
  readonly id: string;
  readonly schoolId: string;
  readonly personId: string;
  readonly displayName: string;
  readonly specialty: string | null;
  readonly bio: string | null;
  readonly subjectIds: readonly string[];
  readonly archivedAt: string | null;
}

// --- Course sections ("Cursos") --------------------------------------------------

const termField = z.string().trim().min(1).max(50);

export const createCourseSectionRequestSchema = z.object({
  subjectId: z.uuid(),
  campusId: z.uuid().nullish(),
  teacherId: z.uuid().nullish(),
  term: termField,
  schedule: z.string().trim().max(200).nullish(),
  /** Null means no limit. Zero would mean a section nobody can join. */
  capacity: z.number().int().positive().nullish(),
});
export type CreateCourseSectionRequest = z.infer<typeof createCourseSectionRequestSchema>;

export const updateCourseSectionRequestSchema = z
  .object({
    teacherId: z.uuid().nullish(),
    term: termField.optional(),
    schedule: z.string().trim().max(200).nullish(),
    capacity: z.number().int().positive().nullish(),
    campusId: z.uuid().nullish(),
    status: z.enum(COURSE_SECTION_STATUSES).optional(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateCourseSectionRequest = z.infer<typeof updateCourseSectionRequestSchema>;

export const archiveCourseSectionRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type ArchiveCourseSectionRequest = z.infer<typeof archiveCourseSectionRequestSchema>;

export const listCourseSectionsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  schoolId: z.uuid().nullish(),
  subjectId: z.uuid().nullish(),
  status: z.enum(COURSE_SECTION_STATUSES).nullish(),
  campusId: z.uuid().nullish(),
  /** Only sections with room for one more student — what the self-enroll
   *  browse page filters by. */
  hasOpenCapacity: queryBoolean().default(false),
  includeArchived: queryBoolean().default(false),
});
export type ListCourseSectionsQuery = z.infer<typeof listCourseSectionsQuerySchema>;

// --- Enrollments ----------------------------------------------------------------

export const enrollPersonRequestSchema = z.object({
  personId: z.uuid(),
});
export type EnrollPersonRequest = z.infer<typeof enrollPersonRequestSchema>;

/**
 * A reason is optional, the same reasoning `removeGroupMemberRequestSchema`
 * gives: withdrawing from a section is ordinary and usually needs no story.
 * A grade may be recorded in the same step a section ends — the common case.
 */
export const endEnrollmentRequestSchema = z.object({
  status: z.enum(['COMPLETED', 'WITHDRAWN']),
  reason: z.string().trim().max(500).nullish(),
  grade: gradeField.nullish(),
});
export type EndEnrollmentRequest = z.infer<typeof endEnrollmentRequestSchema>;

export const recordGradeRequestSchema = z.object({
  grade: gradeField,
});
export type RecordGradeRequest = z.infer<typeof recordGradeRequestSchema>;

/**
 * A roster entry. `displayName` is resolved through PeopleReadPort at read
 * time, never copied onto the row — the same reasoning `GroupMemberResponse`
 * documents. `letterGrade` is derived from `finalGrade` at response time
 * (90+ A, 80+ B, 70+ C, 60+ D, below F), never stored.
 */
export interface EnrollmentResponse {
  readonly personId: string;
  readonly displayName: string;
  readonly status: EnrollmentStatus;
  readonly selfEnrolled: boolean;
  readonly finalGrade: number | null;
  readonly letterGrade: string | null;
  readonly enrolledAt: string;
  readonly endedAt: string | null;
}

/** One row of a person's academic history — a real enrollment. Homologated
 *  credit is a separate list (`HomologationResponse`, filtered to
 *  `APPROVED`), not merged into this one, since it never held a roster. */
export interface TranscriptEntryResponse {
  readonly enrollmentId: string;
  readonly courseSectionId: string;
  readonly schoolId: string;
  readonly schoolName: string;
  readonly subjectId: string;
  readonly subjectName: string;
  readonly term: string;
  readonly status: EnrollmentStatus;
  readonly selfEnrolled: boolean;
  readonly finalGrade: number | null;
  readonly letterGrade: string | null;
  readonly enrolledAt: string;
  readonly endedAt: string | null;
}

// --- Responses --------------------------------------------------------------

export interface CourseSectionSummaryResponse {
  readonly id: string;
  readonly schoolId: string;
  readonly subjectId: string;
  readonly subjectName: string;
  readonly status: CourseSectionStatus;
  readonly campusId: string | null;
  readonly teacherId: string | null;
  readonly term: string;
  readonly schedule: string | null;
  readonly capacity: number | null;
  /** Active enrollments only — the number staff actually mean by "how full is it". */
  readonly enrollmentCount: number;
  readonly archivedAt: string | null;
}

export interface CourseSectionDetailResponse extends CourseSectionSummaryResponse {
  readonly teacherDisplayName: string | null;
  readonly enrollments: readonly EnrollmentResponse[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * What `AcademicReadPort` hands another module — Attendance, resolving a
 * class session's context — the same shape `GroupReference` takes for the
 * same reason.
 */
export interface CourseReference {
  readonly id: string;
  readonly name: string;
  readonly isArchived: boolean;
}

// --- Homologations ----------------------------------------------------------------

export const createHomologationRequestSchema = z
  .object({
    personId: z.uuid(),
    subjectId: z.uuid(),
    sourceType: z.enum(HOMOLOGATION_SOURCE_TYPES),
    sourceSchoolId: z.uuid().nullish(),
    sourceInstitutionName: z.string().trim().max(200).nullish(),
    supportingFileId: z.uuid().nullish(),
    grade: gradeField.nullish(),
  })
  .refine(
    (value) =>
      value.sourceType === 'INTERNAL_SCHOOL'
        ? !!value.sourceSchoolId
        : !!value.sourceInstitutionName?.trim(),
    { message: 'Name the source school or the source institution, matching sourceType' },
  );
export type CreateHomologationRequest = z.infer<typeof createHomologationRequestSchema>;

export const reviewHomologationRequestSchema = z.object({
  status: z.enum(['APPROVED', 'REJECTED']),
  reviewNote: z.string().trim().max(500).nullish(),
});
export type ReviewHomologationRequest = z.infer<typeof reviewHomologationRequestSchema>;

export const listHomologationsQuerySchema = z.object({
  schoolId: z.uuid().nullish(),
  personId: z.uuid().nullish(),
  status: z.enum(HOMOLOGATION_STATUSES).nullish(),
});
export type ListHomologationsQuery = z.infer<typeof listHomologationsQuerySchema>;

export interface HomologationResponse {
  readonly id: string;
  readonly schoolId: string;
  readonly personId: string;
  readonly personDisplayName: string;
  readonly subjectId: string;
  readonly subjectName: string;
  readonly sourceType: HomologationSourceType;
  readonly sourceSchoolId: string | null;
  readonly sourceInstitutionName: string | null;
  readonly supportingFileId: string | null;
  readonly grade: number | null;
  readonly status: HomologationStatus;
  readonly reviewedByUserId: string | null;
  readonly reviewedAt: string | null;
  readonly reviewNote: string | null;
  readonly createdAt: string;
}
